import React, { useContext } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Helmet, HelmetProvider } from "react-helmet-async";

import AuthCard from "./pages/AuthCard";
import LandingPage from "./pages/LandingPage";
import DashboardLayout from "./components/DashboardLayout";
import PrivateRoute from "./components/PrivateRoute";
import QuizPage from "./components/QuizPage";
import { UserProvider, UserContext } from "./context/UserContext";
import { ToastProvider } from "./components/Toast";
import NotFoundPage from "./pages/NotFoundPage";
import SearchPage from "./pages/SearchPage";
import "./styles/dark-theme.css";

import ProfilePage from "./components/ProfilePage";

// ===== LIBRARY IMPORTS =====
import Library from "./pages/Library";
import PublicLibrary from "./pages/PublicLibrary";

// ===== OTHER PAGE IMPORTS =====
import FYP from "./components/FYP";

// ===== REVIEW PAGE IMPORT =====
import ReviewPage from "./components/ReviewPage";

// ===== ADMIN IMPORT =====
import AdminDashboard from "./admin/pages/AdminDashboard";
import AdminUsers from "./admin/pages/AdminUsers";
import AdminAdmins from "./admin/pages/AdminAdmins";
import AdminQuestions from "./admin/pages/AdminQuestions";
import AdminSettings from "./admin/pages/AdminSettings";
import AdminLogs from "./admin/pages/AdminLogs";
import AdminNotifications from "./admin/pages/AdminNotifications";
import BulkUpload from "./admin/pages/BulkUpload";
import AdminMessages from "./admin/pages/AdminMessages";
import AdminLibrary from "./admin/pages/AdminLibrary";
import AdminFeed from "./admin/pages/AdminFeed";
import AdminRecycleBin from "./admin/pages/AdminRecycleBin";
import DuplicateQuestions from "./admin/pages/DuplicateQuestions";
import AdminAnnouncements from "./admin/pages/AdminAnnouncements";
import AdminPending from "./admin/pages/AdminPending";
import AdminInstitutions from "./admin/pages/AdminInstitutions";
import AdminDailySummary from "./admin/pages/AdminDailySummary";
import AdminTelegramBot from "./admin/pages/AdminTelegramBot";
import AdminAILogs from "./admin/pages/AdminAILogs";
import AdminPayments from "./admin/pages/AdminPayments";

// ===== USER PAGES =====
import Leaderboard from "./pages/Leaderboard";
import QuizHistory from "./pages/QuizHistory";
import Submit from "./pages/Submit";

// ===== ADMIN LAYOUT =====
import AdminLayout from "./admin/components/AdminLayout";

// ===== AUTH/RESET PASSWORD PAGES =====
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminRecovery from "./pages/AdminRecovery";
import VerifyEmail  from "./pages/VerifyEmail";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import CompleteProfileModal from "./components/CompleteProfileModal";

const BASE = "https://uhcacadamy.com";

// Per-route SEO config — canonical + title + description + indexing
const ROUTE_SEO = {
  "/": {
    title: "UHC Academy | Free Nursing & NMC Exam Prep — Quizzes, Study Hub & Leaderboard",
    description: "Free nursing education and NMC licensing exam preparation platform for students worldwide. Practice adaptive quizzes, access study flashcards, past questions, and library resources — 100% free.",
    index: true,
  },
  "/study-hub": {
    title: "Nursing Study Hub | Free Flashcards, Quick Notes & NMC Prep — UHC Academy",
    description: "Access free nursing study flashcards, concise clinical quick notes, and revision resources for pharmacology, med-surg, pediatrics, and NMC exam prep.",
    index: true,
  },
  "/auth": {
    title: "Sign In or Register | UHC Academy",
    description: "Log in or create your free UHC Academy account to access nursing quizzes, flashcards, and the global leaderboard.",
    index: false,
  },
  "/forgot-password": {
    title: "Forgot Password | UHC Academy",
    description: "Reset your UHC Academy password to regain access to your nursing study account.",
    index: false,
  },
  "/verify-email": { title: "Verify Email | UHC Academy", description: "", index: false },
  "/login":         { title: "Sign In | UHC Academy", description: "", index: false },
};

// Prefixes that should always be noindex
const NOINDEX_PREFIXES = ["/admin", "/dashboard", "/fyp", "/quiz", "/profile",
  "/library", "/search", "/leaderboard", "/quiz-history", "/review",
  "/submit", "/reset-password", "/admin-recovery"];

function PageSEO() {
  const { pathname } = useLocation();
  const seo = ROUTE_SEO[pathname] || null;

  const shouldNoIndex =
    !seo?.index ||
    NOINDEX_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(prefix + "/"));

  const canonical = `${BASE}${pathname === "/" ? "/" : pathname}`;
  const title = seo?.title || "UHC Academy";
  const description = seo?.description || "";

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
      {/* OG + Twitter canonical sync */}
      <meta property="og:url" content={canonical} />
      <meta name="twitter:url" content={canonical} />
    </Helmet>
  );
}

function AppRoutes() {
  const { user, setUser } = useContext(UserContext);
  const location = useLocation();

  // Page transition variants
  const pageVariants = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -12 }
  };

  const pageTransition = {
    type: "tween",
    ease: [0.25, 0.46, 0.45, 0.94],
    duration: 0.4
  };

  return (
    <>
      <PageSEO />
      <CompleteProfileModal />
      <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* ===== LANDING PAGE ===== */}
        <Route path="/" element={
          <motion.div
            initial="initial" animate="animate" exit="exit"
            variants={pageVariants} transition={pageTransition}
            style={{ minHeight: '100vh' }}
          >
            <LandingPage />
          </motion.div>
        } />

        {/* ===== AUTH PAGE ===== */}
        <Route
          path="/auth"
          element={
            <motion.div
              initial="initial" animate="animate" exit="exit"
              variants={pageVariants} transition={pageTransition}
              style={{ minHeight: '100vh' }}
            >
              <AuthCard />
            </motion.div>
          }
        />

        {/* ===== PASSWORD RESET PAGES ===== */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* ===== EMAIL VERIFICATION ===== */}
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* ===== GOOGLE OAUTH CALLBACK ===== */}
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />

        {/* /login alias — VerifyEmail redirects here after success */}
        <Route path="/login" element={
          <motion.div initial="initial" animate="animate" exit="exit"
            variants={pageVariants} transition={pageTransition} style={{ minHeight: '100vh' }}>
            <AuthCard />
          </motion.div>
        } />

        {/* ===== ADMIN RECOVERY (hidden, unlinked from login page) ===== */}
        <Route path="/admin-recovery" element={<AdminRecovery />} />

        {/* ===== ADMIN ROUTES (NESTED UNDER ADMINLAYOUT) ===== */}
        <Route
          path="/admin"
          element={
            <PrivateRoute adminOnly={true}>
              <AdminLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users"         element={<AdminUsers />} />
          <Route path="admins"        element={<AdminAdmins />} />
          <Route path="questions"     element={<AdminQuestions />} />
          <Route path="settings"      element={<AdminSettings />} />
          <Route path="logs"          element={<AdminLogs />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="messages"      element={<AdminMessages />} />
          <Route path="userlibrary"   element={<AdminLibrary />} />
          <Route path="feed"          element={<AdminFeed />} />
          <Route path="recycle-bin"   element={<AdminRecycleBin />} />
          <Route path="uploads"       element={<BulkUpload />} />
          <Route path="duplicates"    element={<DuplicateQuestions />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="pending"       element={<AdminPending />} />
          <Route path="institutions"    element={<AdminInstitutions />} />
          <Route path="daily-summary"   element={<AdminDailySummary />} />
          <Route path="telegram-bot"    element={<AdminTelegramBot />} />
          <Route path="ai-logs"         element={<AdminAILogs />} />
          <Route path="payments"        element={<AdminPayments />} />
        </Route>

        {/* ===== PUBLIC STUDY HUB (GUEST ACCESS — static, no conditionals) ===== */}
        <Route path="/study-hub" element={<PublicLibrary />} />

        {/* ===== DASHBOARD LAYOUT WRAPPER ===== */}
        <Route
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          {/* ===== FYP ===== */}
          <Route path="/dashboard" element={<FYP />} />
          <Route path="/fyp" element={<FYP />} />

          {/* ===== QUIZ ===== */}
          <Route path="/quiz" element={<QuizPage />} />

          {/* ===== REVIEW PAGE ===== */}
          <Route path="/review" element={<ReviewPage />} />

          {/* ===== PROFILE ===== */}
          <Route
            path="/profile"
            element={<ProfilePage user={user} setUser={setUser} />}
          />

          {/* ===== LIBRARY / STUDY HUB ===== */}
          <Route path="/library" element={<Library />} />

          {/* ===== SEARCH ===== */}
          <Route path="/search" element={<SearchPage />} />

          {/* ===== LEADERBOARD ===== */}
          <Route path="/leaderboard" element={<Leaderboard />} />

          {/* ===== QUIZ HISTORY ===== */}
          <Route path="/quiz-history" element={<QuizHistory />} />

          {/* ===== SUBMIT ===== */}
          <Route path="/submit" element={<Submit />} />
        </Route>

        {/* ===== 404 ===== */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <UserProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </UserProvider>
    </HelmetProvider>
  );
}

console.log("%c UHC DEPLOY V3.4.1 ACTIVE ", "background: red; color: white; font-weight: bold; font-size: 20px;");

export default App;